import { writable, derived } from 'svelte/store';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { navigationActions } from '../stores/navigation';

// Auth state stores
export const session = writable<Session | null>(null);
export const user = writable<User | null>(null);
export const isLoading = writable<boolean>(true);
export const authError = writable<string | null>(null);

// Derived state
export const isAuthenticated = derived(
  [user, session],
  ([$user, $session]) => !!$user && !!$session
);

// Auth actions
export const authActions = {
  // Initialize auth state on app start
  initialize: async () => {
    try {
      isLoading.set(true);
      authError.set(null);

      // Get initial session
      const {
        data: { session: initialSession },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        console.error('Error getting session:', error);
        authError.set(error.message);
      } else {
        session.set(initialSession);
        user.set(initialSession?.user ?? null);
      }

      // Listen for auth changes
      let previousUserId: string | null = null;

      supabase.auth.onAuthStateChange((event, currentSession) => {
        const currentUserId = currentSession?.user?.id ?? null;

        // Log only meaningful auth events (skip silent token refreshes)
        if (event !== 'TOKEN_REFRESHED' && event !== 'SIGNED_IN') {
          console.log('Auth state changed:', event, currentSession?.user?.email);
        }

        // Only update stores if user actually changed (prevents re-renders on token refresh)
        if (event === 'TOKEN_REFRESHED' && currentUserId === previousUserId) {
          // Silent token refresh - don't update stores
          return;
        }

        // Update stores for real auth changes
        session.set(currentSession);
        user.set(currentSession?.user ?? null);
        previousUserId = currentUserId;

        if (event === 'SIGNED_OUT') {
          authError.set(null);
          previousUserId = null;
        }
      });
    } catch (err) {
      console.error('Auth initialization error:', err);
      authError.set(err instanceof Error ? err.message : 'Failed to initialize auth');
    } finally {
      isLoading.set(false);
    }
  },

  // Sign in with Google OAuth
  signInWithGoogle: async () => {
    try {
      authError.set(null);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      });

      if (error) {
        authError.set(error.message);
        return false;
      }
      return true;
    } catch (err) {
      authError.set(err instanceof Error ? err.message : 'Failed to sign in with Google');
      return false;
    }
  },

  // Check if email exists in database
  checkEmailExists: async (email: string): Promise<boolean> => {
    try {
      // Try to sign in with a dummy password to check if email exists
      const { error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase(),
        password: '__dummy_password_for_check__',
      });

      if (error) {
        // Check the error message to determine if email exists
        if (
          error.message.includes('Invalid login credentials') ||
          error.message.includes('Wrong password') ||
          error.message.includes('Invalid password')
        ) {
          // Email exists but password is wrong
          return true;
        } else {
          // Email doesn't exist or other error
          return false;
        }
      } else {
        // Unlikely but if somehow dummy password worked, email exists
        return true;
      }
    } catch {
      // If there's an error, assume email doesn't exist
      return false;
    }
  },

  // Sign up new user
  signUp: async (email: string, password: string) => {
    try {
      authError.set(null);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) {
        authError.set(error.message);
        return { success: false, needsConfirmation: false, error: error.message };
      }

      // If user was created and we have a session, try to initialize user data
      // This is a fallback in case the database trigger didn't work
      if (data.user && data.session) {
        try {
          // Call RPC function to ensure user data is initialized
          // This will be created in the database with SECURITY DEFINER
          await supabase.rpc('ensure_user_initialized');
        } catch (initError) {
          // Log but don't fail registration if initialization fails
          // The trigger should handle this anyway
          console.warn('User initialization warning:', initError);
        }
      }

      // Check if user needs email confirmation
      const needsConfirmation = !data.session && data.user && !data.user.email_confirmed_at;

      return {
        success: true,
        needsConfirmation,
        user: data.user,
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign up';
      authError.set(errorMessage);
      return { success: false, needsConfirmation: false, error: errorMessage };
    }
  },

  // Sign in existing user
  signIn: async (email: string, password: string) => {
    try {
      authError.set(null);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        authError.set(error.message);
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign in';
      authError.set(errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  // Reset password
  resetPassword: async (email: string) => {
    try {
      authError.set(null);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        authError.set(error.message);
        return false;
      }
      return true;
    } catch (err) {
      authError.set(err instanceof Error ? err.message : 'Failed to send reset email');
      return false;
    }
  },

  // Sign out
  signOut: async () => {
    try {
      authError.set(null);
      const { error } = await supabase.auth.signOut();

      if (error) {
        authError.set(error.message);
        return false;
      }
      return true;
    } catch (err) {
      authError.set(err instanceof Error ? err.message : 'Failed to sign out');
      return false;
    }
  },

  // Clear auth error
  clearError: () => {
    authError.set(null);
  },

  // Update user profile (metadata)
  updateProfile: async (updates: {
    full_name?: string;
    display_name?: string;
    avatar_url?: string;
  }) => {
    try {
      authError.set(null);
      const { data, error } = await supabase.auth.updateUser({
        data: updates,
      });

      if (error) {
        authError.set(error.message);
        return { success: false, error: error.message };
      }

      // Update local user state
      user.set(data.user);
      return { success: true, user: data.user };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update profile';
      authError.set(errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  // Update password
  updatePassword: async (newPassword: string) => {
    try {
      authError.set(null);
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        authError.set(error.message);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update password';
      authError.set(errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  // Get auth method (email or google)
  getAuthMethod: (currentUser: User | null): 'email' | 'google' | 'unknown' => {
    if (!currentUser) return 'unknown';

    const providers = currentUser.app_metadata?.providers || [];

    if (providers.includes('google')) {
      return 'google';
    } else if (providers.includes('email')) {
      return 'email';
    }

    return 'unknown';
  },

  // Delete user account
  deleteAccount: async (reason?: string, backupRequested: boolean = true) => {
    try {
      authError.set(null);

      // Get current user info before deletion
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();

      if (!currentUser) {
        authError.set('Není přihlášený uživatel');
        return { success: false, error: 'Není přihlášený uživatel' };
      }

      // Save deletion reason to database
      const { error: insertError } = await supabase.from('account_deletion_reasons').insert({
        user_id: currentUser.id,
        user_email: currentUser.email,
        reason: reason || null,
        backup_requested: backupRequested,
      });

      if (insertError) {
        console.error('Error saving deletion reason:', insertError);
        // Continue with deletion even if saving reason fails
      }

      // Delete the user account using admin API
      // Note: This requires the RPC function to be set up in Supabase
      const { error: deleteError } = await supabase.rpc('delete_user_account');

      if (deleteError) {
        authError.set(deleteError.message);
        return { success: false, error: deleteError.message };
      }

      // Sign out after successful deletion
      await authActions.signOut();

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Nepodařilo se smazat účet';
      authError.set(errorMessage);
      return { success: false, error: errorMessage };
    }
  },
};
